import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { TicketCategoryModal } from './TicketCategoryModal'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import { Currency, Languages } from '../../../common/models'
import {
  TicketCategory,
  TicketCategoryDetails,
} from '../../../common/models/ticket'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsLanguages,
} from '../../../core/services/festival-settings'
import {
  getAllCategories,
  getCategory,
  publishTicketCategory,
  unpublishTicketCategory,
} from '../../../core/services/tickets'
import { useAppDispatch, useAppSelector } from '../../../store'
import { setCategoryInRedux } from '../../ducks/tickets'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export const TicketCategoriesPage: React.FC = () => {
  const { sortBy, size, setSortBy } = useTableParams({
    defaultSize: 50,
  })

  const [editCreateModal, setEditCreateModal] = useState(false)
  const [item, setItem] = useState<TicketCategoryDetails>()
  const [ticketCategories, setTicketCategories] = useState<TicketCategory[]>([])
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const { hasEditPermission } = useCheckPermission({ page: 'TICKETS_CATEGORY' })

  const dispatch = useAppDispatch()
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const [currencyResponse, allLanguagesResponse] = await Promise.all([
          getCurrencies(),
          getSettingsLanguages(),
        ])
        if (currencyResponse.status === 200 && allLanguagesResponse.status === 200) {
          setCurrency(currencyResponse.data.defaultCurrency)
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
          
        }
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAllCategories({ size, sortBy, sortDirection })
      setTicketCategories(response.data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [size, sortBy, sortDirection])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handlePublish = useCallback(
    async (isCheck: string[]) => {
      if (!hasEditPermission) {
        return
      }
      setLoading(true)
      try {
        const { status } = await publishTicketCategory(isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData, hasEditPermission],
  )

  const handleUnpublish = useCallback(
    async (isCheck: string[]) => {
      if (!hasEditPermission) {
        return
      }
      setLoading(true)
      try {
        const { status } = await unpublishTicketCategory(isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData, hasEditPermission],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'ID',
        accessor: 'readableId',
        sortable: true,
      },
      {
        title: 'Name',
        accessor: 'name',
        sortable: true,
      },
      {
        title: 'Ordinal',
        accessor: 'ordinal',
        sortable: true,
        render: (row) => <input disabled value={row.ordinal} />,
      },
      {
        title: 'Published',
        accessor: '',
        render: (row) => (
          <Checkbox
            checked={row.published}
            disabled={!hasEditPermission}
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
          />
        ),
      },
      {
        title: 'Options',
        accessor: '',
        render: (row) => (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                disabled
                checked={['BOTH', 'FULL_PRICE', 'MONTHLY'].includes(
                  row.paymentOption,
                )}
              />
              <span>Payments: {row.paymentOption || 'none'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox disabled checked={row.hasBonus} />
              <span>Bonus</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox disabled checked={row.hasReferral} />
              <span>Referral</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox disabled checked={row.hasDiscount} />
              <span>Discount</span>
            </div>
          </div>
        ),
      },
      {
        title: 'Published Date',
        accessor: 'publishedDate',
        sortable: true,
        render: (row) => formatDateTime(row.publishTimestamp),
      },
      {
        title: 'Details',
        accessor: '',
        render: (row) => (
          <button
            type="button"
            className="view pt-2 text-accent-1 underline"
            onClick={() => {
              if (editionId) {
                dispatch(setCategoryInRedux(editionId, row.id))
                navigate(
                  `/tickets/tickets/${festivalName}/${editionId}/${row.id}`,
                )
              }
            }}
          >
            View All Tickets
          </button>
        ),
        width: '120px',
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row) => (
          <button
            type="button"
            className="view pt-2 text-accent-1 underline"
            onClick={async () => {
              if (!hasEditPermission) {
                return
              }
              setEditCreateModal(true)
              const response = await getCategory(row.id)
              setItem(response.data)
            }}
          >
            Edit
          </button>
        ),
        width: '80px',
      },
    ],
    [
      dispatch,
      editionId,
      festivalName,
      handlePublish,
      handleUnpublish,
      hasEditPermission,
      navigate,
    ],
  )

  return (
    <>
      <Header
        pageTitle="Tickets Categories"
        actions={
          <>
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditCreateModal(true)
                  setItem(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Tickets" subtitle="Categories" className="mb-5" />
        <Table
          columns={columns}
          handleSortBy={handleSortBy}
          data={ticketCategories}
          columnWidth="15%"
        />
      </Card>
      <TicketCategoryModal
        currency={currency?.symbol || 'RON'}
        visible={editCreateModal}
        setVisible={setEditCreateModal}
        item={item}
        setTicketCategories={setTicketCategories}
        sortBy={sortBy}
        allLanguages={allLanguages}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
